import React, { useContext } from 'react';
import TextButton, { TextButtonProps } from '../../../Buttons/TextButton';
import omit from 'lodash/omit';
import { useDropdownToggleProps } from '../../hooks/useDropdownToggleProps';
import { DropdownStateContext } from '../../DropdownStateManager';
import type { ComponentRequiresAriaHasPopup } from '../types';

interface OpenOrClosedPostfix {
  opened: TextButtonProps['postfix'];
  closed: TextButtonProps['postfix'];
}

export interface Props
  extends Omit<TextButtonProps, 'postfix' | 'aria-haspopup'>, ComponentRequiresAriaHasPopup {
  postfix?: TextButtonProps['postfix'] | OpenOrClosedPostfix;
}

function usePostfixIcon({ postfix }: Props): TextButtonProps['postfix'] | undefined {
  const { isOpen } = useContext(DropdownStateContext);

  if (postfix && (postfix as OpenOrClosedPostfix).opened) {
    return isOpen
      ? (postfix as OpenOrClosedPostfix).opened
      : (postfix as OpenOrClosedPostfix).closed;
  }
  return postfix as TextButtonProps['postfix'];
}

/**
 * A wrapper around our DS `TextButton` that provides the props for a dropdown toggle.
 *
 * Note: This component is only the toggle! It doesn't contain a popover or dropdown. If you're looking for something that does,
 * you probably want a preset instead of this. Consider `TextButtonWithContextMenu`.
 *
 * Required contexts: `PopperDropdownContext`, `DropdownStateContext`
 */
const PopperTextToggleButton: React.FC<Props> = props => {
  const necessaryToggleProps = useDropdownToggleProps();
  const postfixIcon = usePostfixIcon(props);
  return <TextButton postfix={postfixIcon} {...necessaryToggleProps} {...omit(props, 'postfix')} />;
};
PopperTextToggleButton.displayName = 'PopperTextToggleButton';

export default PopperTextToggleButton;
